import { useState } from 'react';

import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { RoleChecklist } from '@/features/roles/components/RoleChecklist';
import { useAddMemberMutation } from '@/features/orgs/api/orgApi';
import { useGetRolesQuery } from '@/features/roles/api/rolesApi';
import { mutationErrorMessage } from '@/features/common/apiError';
import { useToast } from '@/components/shared/Toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { OrgRole } from '../types';

/** Design §10.1 — the helper copy lives at the point of decision (D3/D4). */
const ORG_ROLE_OPTIONS: { value: OrgRole; label: string; helper: string }[] = [
  { value: 'member', label: 'Member', helper: 'Member — everyday access' },
  { value: 'admin', label: 'Admin', helper: 'Admin — manage members and roles' },
  { value: 'owner', label: 'Owner', helper: 'Owner — full control' },
];

export interface AddMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
}

/**
 * Add member dialog (design §10.1). There is no user-lookup endpoint in the P1
 * set (§3.1), so the user id is pasted into a monospace field whose placeholder
 * teaches the shape. Submit is `POST /organizations/:id/members` with
 * `{userId, orgRole, roleIds}` — the service accepts both the org role and the
 * optional role ids, and validates the role ids server-side.
 */
export function AddMemberDialog({ open, onOpenChange, orgId }: AddMemberDialogProps) {
  const toast = useToast();
  const [userId, setUserId] = useState('');
  const [orgRole, setOrgRole] = useState<OrgRole>('member');
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [addMember, { isLoading }] = useAddMemberMutation();
  const { data: roles = [], isLoading: rolesLoading } = useGetRolesQuery(undefined, {
    skip: !open,
  });

  const roleHelper = ORG_ROLE_OPTIONS.find((option) => option.value === orgRole)?.helper;

  function reset() {
    setUserId('');
    setOrgRole('member');
    setRoleIds([]);
    setFieldError(null);
  }

  async function handleSubmit() {
    const trimmed = userId.trim();
    if (!trimmed) {
      setFieldError("Enter the user's ID.");
      return;
    }
    setFieldError(null);

    try {
      await addMember({
        orgId,
        userId: trimmed,
        orgRole,
        roleIds: roleIds.length > 0 ? roleIds : undefined,
      }).unwrap();
      toast.success('Member added.');
      reset();
      onOpenChange(false);
    } catch (error) {
      toast.error(mutationErrorMessage(error, "Couldn't add member."));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isLoading) {
          onOpenChange(next);
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add member</DialogTitle>
          <DialogDescription>Give someone access to this organization.</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="member-user-id">User ID</Label>
            <Input
              id="member-user-id"
              value={userId}
              onChange={(event) => {
                setUserId(event.target.value);
                if (fieldError) {
                  setFieldError(null);
                }
              }}
              placeholder="e.g. usr_8f3k2q9v"
              className="font-mono text-xs"
              disabled={isLoading}
              aria-invalid={fieldError ? true : undefined}
              aria-describedby={fieldError ? 'member-user-id-error' : 'member-user-id-help'}
            />
            {fieldError ? (
              <p
                id="member-user-id-error"
                role="alert"
                className="text-sm text-destructive-foreground bg-destructive/10 px-3 py-2 rounded-md"
              >
                {fieldError}
              </p>
            ) : (
              <p id="member-user-id-help" className="text-sm text-muted-foreground">
                The user's ID. Ask the user or your administrator.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="member-org-role">Organization role</Label>
            <Select
              value={orgRole}
              disabled={isLoading}
              onValueChange={(value) => setOrgRole(value as OrgRole)}
            >
              <SelectTrigger id="member-org-role" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ORG_ROLE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">{roleHelper}</p>
          </div>

          <div className="space-y-2">
            <Label>Roles (optional)</Label>
            <RoleChecklist
              roles={roles}
              selectedRoleIds={roleIds}
              onToggle={(roleId, checked) =>
                setRoleIds((current) =>
                  checked ? [...current, roleId] : current.filter((value) => value !== roleId),
                )
              }
              isLoading={rolesLoading}
              disabled={isLoading}
            />
            <p className="text-sm text-muted-foreground">
              Optional. Roles grant time-limited policies.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? <LoadingSpinner size="sm" className="mr-2" label="Adding member" /> : null}
            {isLoading ? 'Adding…' : 'Add member'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
